/**
 * How players pay for a league (payments phase 2c): free, a fee per team for
 * the season, or a fee per player for each game night. Shown only while
 * payments are on; the server checks the host's Stripe and locks the fee
 * once anything is paid.
 */

import React from 'react';
import { PriceHint, dollarsToCents, centsToDollars, priceError, usePaymentsSetup } from '../payments/PriceField';
import { RefundPolicyField } from '../payments/RefundPolicy';

export const FEE_MODE_OPTIONS = [
  { value: 'none', label: 'Free', payer: 'Players' },
  { value: 'team_season', label: 'Each team pays per season', payer: 'Teams' },
  { value: 'player_night', label: 'Each player pays per game night', payer: 'Players' },
];

/** The fee as the form keeps it, from a league row. */
export const feeFromLeague = (league) => ({
  mode: league?.fee_mode || 'none',
  price: centsToDollars(Number(league?.fee_cents) || 0),
  policy: league?.refund_policy || '',
});

/** The fee fields for the API, and an error when the price won't do. */
export const feeForApi = (fee) => {
  const policy = { refund_policy: (fee.policy || '').trim() || null };
  if (fee.mode === 'none') return { fields: { fee_mode: 'none', fee_cents: 0, ...policy }, error: '' };
  const cents = dollarsToCents(fee.price);
  if (priceError(fee.price) || !(cents > 0)) {
    return { fields: null, error: 'League fee: enter $1 to $10,000, or choose Free' };
  }
  return { fields: { fee_mode: fee.mode, fee_cents: cents, ...policy }, error: '' };
};

function LeagueFeeFields({ fee, onChange }) {
  const setup = usePaymentsSetup();
  if (!setup.enabled) return null;
  const option = FEE_MODE_OPTIONS.find((o) => o.value === fee.mode) || FEE_MODE_OPTIONS[0];

  return (
    <div className="form-group-gaming" data-testid="league-fee-fields">
      <label htmlFor="league-fee-mode" className="form-label-gaming">League fee</label>
      <select
        id="league-fee-mode"
        value={fee.mode}
        onChange={(e) => onChange({ ...fee, mode: e.target.value })}
        className="form-select-gaming"
      >
        {FEE_MODE_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
      </select>
      {fee.mode !== 'none' && (
        <>
          <label htmlFor="league-fee-price" className="form-label-gaming" style={{ marginTop: '0.6rem' }}>
            {fee.mode === 'team_season' ? 'Fee per team for the season (CAD)' : 'Fee per player per game night (CAD)'}
          </label>
          <input
            id="league-fee-price"
            type="number"
            min="1"
            step="0.01"
            value={fee.price}
            onChange={(e) => onChange({ ...fee, price: e.target.value })}
            className="form-input-gaming"
          />
          <PriceHint value={fee.price} payer={option.payer} setup={setup} />
          <RefundPolicyField
            value={fee.policy}
            price={fee.price}
            setup={setup}
            onChange={(policy) => onChange({ ...fee, policy })}
          />
        </>
      )}
    </div>
  );
}

export default LeagueFeeFields;
